import { List, Map, ViewModule } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";

// Decoupled view switcher for RubiALERT (card / list / map). Reads the current
// view and reports changes via onChange; the parent owns the view state (URL
// param) and the actual view rendering plus any view <-> filter coupling
// (e.g. map view geo bounds).
type AlertView = "card" | "list" | "map";

interface AlertViewTabsProps {
  view: string;
  defaultView: AlertView;
  activeViews: AlertView[];
  onChange: (value: AlertView) => void;
}

const iconMapping = {
  card: <ViewModule style={{ marginRight: "4px" }} />,
  list: <List style={{ marginRight: "4px" }} />,
  map: <Map style={{ marginRight: "4px" }} />,
};

const tabSx = (active: boolean) => ({
  marginRight: "24px",
  textTransform: "none" as const,
  fontWeight: active ? "bold" : "normal",
  color: active ? "primary.main" : "action.active",
  borderBottom: active ? "2px solid var(--primary-main)" : "none",
  paddingBottom: "6px",
  "&:hover": {
    borderBottom: "2px solid var(--primary-main)",
    paddingBottom: "6px",
  },
});

const AlertViewTabs = ({
  view,
  defaultView,
  activeViews,
  onChange,
}: AlertViewTabsProps) => {
  const { t } = useTranslation("bulletin/searchbar");

  if (!activeViews || activeViews.length <= 1) {
    return (
      <div
        style={{
          borderBottom: "1px solid var(--divider)",
          padding: "10px 0",
        }}
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        borderBottom: "1px solid var(--divider)",
        padding: "10px 0",
        fontSize: "14px",
        color: "action.active",
      }}
    >
      {[defaultView, ...activeViews.filter((v) => v !== defaultView)].map(
        (v) => (
          <Button key={v} sx={tabSx(view === v)} onClick={() => onChange(v)}>
            {iconMapping[v]}
            {t(`${v}View`)}
          </Button>
        ),
      )}
    </div>
  );
};

export default AlertViewTabs;
